// (B) kit provider 호스트 — antd 완전 제거(D4-2). 앱 루트에 한 번 마운트된다.
//   - Radix Tooltip.Provider(오버레이 배치3+)
//   - sonner <Toaster/>(notification/result) + OmMessageHost(message 커스텀 host)
//   - DialogStackHost(D4-2 명령형 Modal.confirm — radix Dialog)
//   - ConfirmDefaultsContext(owma i18next → confirm 기본 OK/Cancel 텍스트 공급, kit 은 i18n 미보유)
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { Toaster } from 'sonner'
import { useState, useEffect, useSyncExternalStore, type ReactNode } from 'react'
import { ConfirmDefaultsContext, DialogStackHost, type ConfirmDefaults } from './DialogStack'
import { OmwMessageHost } from './OmwMessageHost'
import { toastIcons } from './toastIcons'
import { notificationStack, NOTIFICATION_TOASTER_ID, RESULT_TOASTER_ID } from './toastStack'
import '../styles/omwTokens.css'
import '../styles/omwToast.css'

// antd Tooltip 기본 표시 지연(mouseEnterDelay 0.1s)에 맞춘 delayDuration.
const TOOLTIP_DELAY_MS = 100

// antd notification stack threshold: 활성 개수가 이 값 이하면 펼침(expand), 초과면 겹침(hover 시 펼침).
const STACK_THRESHOLD = 3

// sonner visibleToasts 기본값은 3 → 화면 높이에 맞춰 더 많이 보이도록 뷰포트에서 산출.
// notification 카드(대략 116px) + gap(16px) 슬롯 기준의 보수적 추정치(오버플로 방지), 최소 3개.
const ESTIMATED_TOAST_SLOT = 132
// 상단 오프셋(top 24px) + 약간의 여백만 예약(하단 여백은 계산 제외).
const VIEWPORT_EDGE_RESERVE = 28
const MIN_VISIBLE_TOASTS = 3

function computeVisibleToasts(): number {
  if (typeof window === 'undefined') return MIN_VISIBLE_TOASTS
  const fit = Math.floor((window.innerHeight - VIEWPORT_EDGE_RESERVE) / ESTIMATED_TOAST_SLOT)
  return Math.max(MIN_VISIBLE_TOASTS, fit)
}

// 뷰포트 높이에 맞춘 visibleToasts. resize 시 재계산(listener 는 unmount 시 정리).
function useVisibleToasts(): number {
  const [count, setCount] = useState(computeVisibleToasts)
  useEffect(() => {
    const onResize = (): void => setCount(computeVisibleToasts())
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  return count
}

interface OmwKitThemeHostProps {
  confirmDefaults?: ConfirmDefaults
  children: ReactNode
}

const FALLBACK_DEFAULTS: ConfirmDefaults = { okText: 'OK', cancelText: 'Cancel' }

export function OmwKitThemeHost({ confirmDefaults, children }: OmwKitThemeHostProps) {
  const visibleToasts = useVisibleToasts()
  // 활성 notification 개수를 구독 → threshold(3) 이하면 펼침, 초과면 겹침(hover 시 펼침).
  const notificationCount = useSyncExternalStore(
    notificationStack.subscribe,
    notificationStack.getCount,
    () => 0,
  )
  const notificationExpand = notificationCount <= STACK_THRESHOLD
  return (
    <TooltipPrimitive.Provider delayDuration={TOOLTIP_DELAY_MS}>
      <ConfirmDefaultsContext.Provider value={confirmDefaults ?? FALLBACK_DEFAULTS}>
        {children}
        {/* 명령형 Modal.confirm 스택 렌더러. realm(window)별 KitThemeHost 가 1개씩 → main/popup 자동 격리. */}
        <DialogStackHost />
      </ConfirmDefaultsContext.Provider>
      {/* message: antd message 파리티 커스텀 host(top-center, 최신이 맨 아래에 append). sonner 는
          토스트를 absolute+translateY 스택으로 배치해 최신을 top 엣지(위)에 고정 → 순서 역전 불가라
          message 만 별도 host(block flow, grid-rows collapse 애니메이션)로 렌더한다(§OmMessageHost). */}
      <OmwMessageHost />

      {/* ⚠ notification/result 는 각각 별도 Toaster(toasterId 라우팅)로 분리 → hover·collapse·expand 상태가
          서로 독립(한 Toaster hover 가 다른 쪽에 영향 없음). icons 는 antd filled 컬러 아이콘 파리티, gap 은
          스택 카드 사이 세로 간격(sonner JS 가 inline --offset 계산에 사용). */}

      {/* notification: top-right 카드. threshold(3) 이하면 펼침, 초과면 겹침(hover 시 sonner 내장 transition
          으로 펼침) — antd notification stack 재현. visibleToasts 는 뷰포트 높이 기준. */}
      <Toaster
        id={NOTIFICATION_TOASTER_ID}
        // 두 Toaster 가 같은 기본 aria-label section 을 만들면 axe landmark-unique 위반 → 구분 라벨 부여.
        containerAriaLabel="Notifications"
        position="top-right"
        expand={notificationExpand}
        gap={16}
        visibleToasts={visibleToasts}
        icons={toastIcons}
      />

      {/* showResult 바·export 진행상태 등 open() 커스텀 토스트 전용. notification/message 와 별도 Toaster 라
          다른 토스트 hover(expand 전환)에 영향받지 않는다. 항상 expand(겹침/스케일 변화 없음). */}
      <Toaster
        id={RESULT_TOASTER_ID}
        containerAriaLabel="Progress notifications"
        position="bottom-center"
        expand
        gap={16}
        visibleToasts={visibleToasts}
        icons={toastIcons}
      />
    </TooltipPrimitive.Provider>
  )
}
